import React, { useState, useEffect } from 'react';
import { 
  Paperclip, 
  Upload, 
  Download, 
  Eye, 
  X, 
  AlertTriangle,
  Trash2
} from 'lucide-react';
import { Modal, ConfigProvider, Button, message, List, Space, Typography, Spin } from 'antd';
import { uploadFileToProjectHQTaskApi, getProjectHQTaskAttachmentsApi, deleteProjectHQTaskAttachmentApi } from '@/services/apis';

interface Attachment {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
}

interface AttachmentManagerProps {
  isOpen: boolean;
  task: API.PROJECTHQ.TypeTask | null;
  onClose: () => void;
}

export const AttachmentManager: React.FC<AttachmentManagerProps> = ({
  isOpen,
  task,
  onClose,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [attachments, setAttachments] = useState<API.DDSFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // 从接口获取附件列表
  useEffect(() => {
    if (isOpen) {
      fetchAttachments();
    }
  }, [isOpen, task]);

  
  const fetchAttachments = async () => {
    if (!task) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectHQTaskAttachmentsApi(task.ID);
      setAttachments(data || []);
    } catch (err) {
      setError('获取附件列表失败');
      console.error('获取附件列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 处理文件删除
  const handleDeleteAttachment = (attachment: API.DDSFile) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个文件吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await handleDeleteAttachmentReq(attachment);
          // 删除成功后刷新列表
          fetchAttachments();
        } catch (error) {
          messageApi.error('文件删除失败');
          console.error('删除文件失败:', error);
        }
      }
    });
  };

  // 预览文件
  const handlePreview = (url: string) => {
    if (!url) {
      messageApi.warning('文件路径不存在');
      return;
    }
    window.open(url, '_blank');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(!task || uploading) return;
    const files = e.target.files;
    if (files && files.length > 0) {
      handleUploadAttachment(files[0]);
      // 重置文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadAttachment = (file: File) => {
    if(!task || uploading) return;
    console.log('上传附件:', task.ID, file.name);
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    uploadFileToProjectHQTaskApi(task.ID, formData).then((res) => {
      console.log('上传成功:', res);
      messageApi.success('上传成功');
      fetchAttachments();
    }).catch((err) => {
      console.error('上传失败:', err);
      setError('上传失败');
      messageApi.error('上传失败');
    }).finally(() => {
      setUploading(false);
    });
  };

  const handleDownloadAttachment = (attachment: any) => {
    console.log('下载附件:', attachment);
  };

  const handleDeleteAttachmentReq = async (attachment: API.DDSFile) => {
    try {
      await deleteProjectHQTaskAttachmentApi(attachment.ID);
      // 删除成功后刷新列表
      setAttachments(attachments.filter(file => file.ID !== attachment.ID));
      messageApi.success('文件删除成功');
    } catch (err) {
      messageApi.error('删除附件失败');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      {contextHolder}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-800">DDS附件管理</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded flex items-center">
            <AlertTriangle size={16} className="mr-2" />
            {error}
          </div>
        )}

        {/* 上传按钮 */}
        <div className="mb-4">
          <button 
            onClick={handleUploadClick}
            disabled={uploading}
            className={`w-full py-2 px-4 bg-primary text-white rounded transition-colors flex items-center justify-center ${uploading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary/90'}`}
          >
            {uploading ? (
              <>
                <Spin size="small" className="mr-2" />
                上传中...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                上传DDS附件
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
        </div>

        {/* 附件列表 */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-600 mb-3">DDS附件列表</h4>
          {loading ? (
            <div className="text-center py-4 text-gray-500">加载中...</div>
          ) : attachments.length === 0 ? (
            <div className="text-center py-4 text-gray-500">暂无附件</div>
          ) : (
            <div className="space-y-3 max-h-[300px] overflow-auto">
              {attachments.map((attachment) => (
                <div key={attachment.ID} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <Paperclip size={16} className="mr-2 text-gray-400" />
                    <div className="text-sm font-medium text-gray-800">{attachment.filename}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => handlePreview(attachment.ddsFilePath)}
                      className="p-1.5 text-primary hover:bg-primary/10 rounded"
                      title="查看dds附件"
                    >
                      <Eye size={16} />
                    </button>
                    {/* <button 
                      onClick={() => handleDownloadAttachment(attachment)}
                      className="p-1.5 text-gray-500 hover:bg-gray-200 rounded"
                      title="下载附件"
                    >
                      <Download size={16} />
                    </button> */}
                    <button 
                      onClick={() => handleDeleteAttachment(attachment)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      title="删除附件"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
